@if (loading) {
  <div class="row">
    <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>
  </div>
}

@if (!loading && surveyDashboard) {
  <section class="rm my-4">
    <header class="rm-header">
      <div class="rm-title-row">
        <div class="rm-title-icon">
          <mat-icon>fact_check</mat-icon>
        </div>
        <div>
          <h4 class="rm-title">{{ 'modals.modal-survey-details.revisions-management' | translate }}</h4>
          <p class="rm-subtitle">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.description' | translate }}</p>
        </div>
      </div>

      <div class="rm-overview">
        @if (consensusSummary.length > 0) {
          <div class="rm-overview-block">
            <div class="rm-overview-label">
              <mat-icon>functions</mat-icon>
              {{ 'components.surveys.survey-dashboard.survey-dashboard.component.advice.algorithmic' | translate }}
              <span class="rm-overview-hint">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.outcomes-hint' | translate }}</span>
            </div>
            <div class="rm-outcomes">
              @for (outcome of consensusSummary; track outcome.advice) {
                <button type="button" class="rm-outcome" [style.--tone]="summaryTone(outcome.advice)"
                  [class.rm-outcome-active]="filters.outcome === outcome.advice"
                  [class.rm-outcome-dimmed]="filters.outcome && filters.outcome !== outcome.advice"
                  (click)="toggleOutcomeFilter(outcome.advice)">
                  <mat-icon class="rm-outcome-icon">{{ adviceIcon(outcome.advice) }}</mat-icon>
                  <span class="rm-outcome-text">
                    <span class="rm-outcome-count">{{ outcome.count }}</span>
                    <span class="rm-outcome-label">{{ outcome.labelKey | translate }}</span>
                  </span>
                  <span class="rm-outcome-perc">{{ outcome.perc | number:'1.0-0' }}%</span>
                </button>
              }
            </div>
          </div>
        }

        <div class="rm-overview-block">
          <div class="rm-overview-label">
            <mat-icon>gavel</mat-icon>
            {{ 'generic.final-decision' | translate }}
          </div>
          <div class="rm-progress">
            <div class="rm-progress-head">
              <span class="rm-progress-count">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.decisions-progress' | translate: { decided: decisionProgress.decided, total: decisionProgress.total } }}</span>
              <span class="rm-progress-percent">{{ decisionProgressPercent }}%</span>
            </div>
            <div class="rm-progress-track">
              @for (segment of decisionSegments; track segment.id) {
                <div class="rm-progress-segment" [style.width.%]="segment.pct" [style.background]="segment.color"
                  [matTooltip]="segment.label + ': ' + segment.count"></div>
              }
            </div>
            <div class="rm-progress-legend">
              @for (segment of decisionSegments; track segment.id) {
                <span><i [style.background]="segment.color"></i>{{ segment.label }} <strong>{{ segment.count }}</strong></span>
              }
              @if (decisionProgress.decided < decisionProgress.total) {
                <span><i class="rm-legend-pending"></i>{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.pending' | translate }} <strong>{{ decisionProgress.total - decisionProgress.decided }}</strong></span>
              }
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="rm-body">
      @for (key of objectKeys(surveyDashboard); track key) {
        @if (surveyDashboard[key]?.questions.data?.length > 0) {
          <div class="rm-section">
            <div class="rm-section-head">
              <h6 class="rm-section-title">
                <mat-icon>topic</mat-icon>
                {{ typologyTitleKey(key) | translate }}
                <span class="rm-section-count">{{ getFilteredQuestions(key).length }} / {{ surveyDashboard[key].questions.data.length }}</span>
              </h6>
            </div>

            <div class="rm-toolbar">
              <label class="rm-search">
                <mat-icon>search</mat-icon>
                <input type="search" [ngModel]="filters.search" (ngModelChange)="updateSearch($event)"
                  [placeholder]="'components.surveys.survey-dashboard.survey-dashboard.component.rm.search' | translate">
              </label>
              <div class="rm-segmented" role="group">
                @for (option of decisionFilterOptions; track option) {
                  <button type="button" [class.rm-segmented-active]="filters.decision === option" (click)="setDecisionFilter(option)">
                    {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.filter-' + option | translate }}
                  </button>
                }
              </div>
              @if (key === 'CONSENSUS') {
                <button type="button" class="rm-toolbar-btn" [class.rm-toolbar-btn-active]="filtersExpanded[key]" (click)="toggleFilters(key)">
                  <mat-icon>tune</mat-icon>
                  {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.advanced-filters' | translate }}
                  @if (advancedFiltersActive > 0) {
                    <span class="rm-badge">{{ advancedFiltersActive }}</span>
                  }
                  <mat-icon>{{ filtersExpanded[key] ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
              }
              @if (anyFilterActive) {
                <button type="button" class="rm-link-btn" (click)="resetFilters()">
                  <mat-icon>filter_alt_off</mat-icon>
                  {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.reset-filters' | translate }}
                </button>
              }
            </div>

            @if (key === 'CONSENSUS' && filtersExpanded[key]) {
              <div class="rm-advanced">
                <div class="rm-advanced-chart">
                  <div echarts [options]="surveyCharts[key]" class="chart w-100"></div>
                </div>
                <div class="rm-advanced-filters">
                  <div class="rm-range">
                    <label>{{ "components.responses.consensus.agree" | translate }} (%)</label>
                    <mat-slider min="0" max="100" step="1" [displayWith]="formatPercent" [discrete]="true">
                      <input matSliderStartThumb name="agree_0_{{key}}" [(ngModel)]="filters.agree[0]" (ngModelChange)="updateFilter(key, 'agree', $event, 0)">
                      <input matSliderEndThumb name="agree_1_{{key}}" [(ngModel)]="filters.agree[1]" (ngModelChange)="updateFilter(key, 'agree', $event, 1)">
                    </mat-slider>
                  </div>
                  <div class="rm-range">
                    <label>{{ "components.responses.consensus.disagree" | translate }} (%)</label>
                    <mat-slider min="0" max="100" step="1" [displayWith]="formatPercent" [discrete]="true">
                      <input matSliderStartThumb name="disagree_0_{{key}}" [(ngModel)]="filters.disagree[0]" (ngModelChange)="updateFilter(key, 'disagree', $event, 0)">
                      <input matSliderEndThumb name="disagree_1_{{key}}" [(ngModel)]="filters.disagree[1]" (ngModelChange)="updateFilter(key, 'disagree', $event, 1)">
                    </mat-slider>
                  </div>
                  <div class="rm-range">
                    <label>{{ "generic.certainty-degree" | translate }} (%)</label>
                    <mat-slider min="0" max="100" step="1" [displayWith]="formatPercent" [discrete]="true">
                      <input matSliderStartThumb name="certainty_0_{{key}}" [(ngModel)]="filters.certainty[0]" (ngModelChange)="updateFilter(key, 'certainty', $event, 0)">
                      <input matSliderEndThumb name="certainty_1_{{key}}" [(ngModel)]="filters.certainty[1]" (ngModelChange)="updateFilter(key, 'certainty', $event, 1)">
                    </mat-slider>
                  </div>
                  <div class="rm-range">
                    <label>{{ "generic.contradiction-degree" | translate }} (%)</label>
                    <mat-slider min="0" max="100" step="1" [displayWith]="formatPercent" [discrete]="true">
                      <input matSliderStartThumb name="contradiction_0_{{key}}" [(ngModel)]="filters.contradiction[0]" (ngModelChange)="updateFilter(key, 'contradiction', $event, 0)">
                      <input matSliderEndThumb name="contradiction_1_{{key}}" [(ngModel)]="filters.contradiction[1]" (ngModelChange)="updateFilter(key, 'contradiction', $event, 1)">
                    </mat-slider>
                  </div>
                  <mat-form-field appearance="outline" subscriptSizing="dynamic">
                    <mat-label>{{ 'generic.section-filter' | translate }}</mat-label>
                    <mat-select name="filter_section" [value]="filters.section" (selectionChange)="updateSectionFilter(key, $event.value)">
                      <mat-option value="">{{ "generic.all" | translate }}</mat-option>
                      @for (sec of keyQuestionsSections(key); track sec) {
                        <mat-option [value]="sec">{{ sec }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline" subscriptSizing="dynamic">
                    <mat-label>{{ 'generic.preliminary-round-assessment-filter' | translate }}</mat-label>
                    <mat-select name="filter_status" [multiple]="true" [value]="filters.preliminary_round_assessment"
                      (selectionChange)="updateStatusFilter(key, $event.value)">
                      @for (sec of keyQuestionsStatuses(key); track sec) {
                        <mat-option [value]="sec">
                          {{ "components.surveys.survey-dashboard.survey-dashboard.component." + (sec | lowercase) | translate }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
              </div>
            }

            <div class="rm-cards">
              @for (group of getPaginatedGroups(key); track group.name) {
              @if (hasSections(key)) {
                <div class="revision-section-header">
                  <mat-icon class="mr-2">folder_open</mat-icon>
                  <span class="revision-section-name">{{ group.name || ('components.surveys.survey-dashboard.survey-dashboard.component.no-section' | translate) }}</span>
                  <span class="revision-section-progress">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.section-decisions' | translate: { decided: group.decided, total: group.total } }}</span>
                </div>
              }
              @for (element of group.items; track element.id) {
                <article class="rm-card" [style.--tone]="key === 'CONSENSUS' ? adviceTone(element) : null"
                  [class.rm-card-decided]="!!element.final_decision">
                  <div class="rm-card-top">
                    <div class="rm-card-meta">
                      <span class="rm-card-number">#{{ questionNumber(key, element) }}</span>
                      @if (element.section) {
                        <span class="rm-card-section" [matTooltip]="element.section">{{ element.section }}</span>
                      }
                    </div>
                    @if (decisionStatus(element); as decided) {
                      <span class="rm-decision-chip" [style.--decision-color]="decided.bgcolor">
                        <mat-icon>{{ decided.icon }}</mat-icon>
                        {{ survey?.is_revised ? decided.title : decided.titleNoRev }}
                      </span>
                    } @else {
                      <span class="rm-pending-chip">
                        <mat-icon>schedule</mat-icon>
                        {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.pending' | translate }}
                      </span>
                    }
                  </div>

                  <p class="rm-card-statement">{{ element.statement || element.question || 'N/A' }}</p>

                  @if (key === 'CONSENSUS') {
                    <div class="rm-insight">
                      <div class="rm-recommendation">
                        <mat-icon class="rm-recommendation-icon">{{ adviceIcon(algorithmicAdvice(element)) }}</mat-icon>
                        <div class="rm-recommendation-body">
                          <div class="rm-recommendation-label">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.advice.algorithmic' | translate }}</div>
                          <div class="rm-recommendation-value">
                            {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.advice-' + algorithmicAdvice(element) | translate }}
                            @if (algorithmicAdvice(element) === 'revise') {
                              <span class="rm-recommendation-status">· {{ consensusStatusKey(element) + '.label' | translate }}</span>
                            }
                          </div>
                          <div class="rm-recommendation-hint">
                            {{ consensusStatusKey(element) + '.action' | translate }}
                          </div>
                          @if (element.analytics_updating) {
                            <div class="rm-recommendation-hint d-flex align-items-center">
                              <mat-icon inline class="mr-1">sync</mat-icon>
                              {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.analytics-updating' | translate }}
                            </div>
                          }
                          @if (algorithmicAdvice(element) !== 'pending') {
                            <div class="rm-recommendation-details">
                              {{ consensusStatusKey(element) + '.explanation' | translate }}
                              @if (paraconsistentMetric(element, 'favorable_evidence_degree') !== null) {
                                <div class="d-flex flex-wrap mt-2">
                                  <span class="mr-3">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.favorable-evidence' | translate }}: <strong>{{ paraconsistentMetric(element, 'favorable_evidence_degree') | number:'1.2-2' }}</strong></span>
                                  <span class="mr-3">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.contrary-evidence' | translate }}: <strong>{{ paraconsistentMetric(element, 'contrary_evidence_degree') | number:'1.2-2' }}</strong></span>
                                  @if (element.paraconsistent?.answer_count) {
                                    <span>{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.computed-on' | translate: { count: element.paraconsistent.answer_count } }}</span>
                                  }
                                </div>
                              }
                            </div>
                          }
                        </div>
                      </div>

                      <div class="rm-consensus-metrics">
                        <div class="rm-votes">
                          <div class="rm-metric-label">{{ 'generic.votes' | translate }}</div>
                          <div class="rm-votes-track">
                            <div class="rm-votes-agree" [style.width.%]="element.agree_perc"></div>
                            <div class="rm-votes-disagree" [style.width.%]="element.disagree_perc"></div>
                          </div>
                          <div class="rm-votes-legend">
                            <span class="rm-agree">{{ 'generic.agree' | translate }} {{ element.agree_perc | number:'1.0-0' }}%</span>
                            @if (!element.required && element.abstained_count) {
                              <span class="rm-abstained">{{ "generic.abstained" | translate }} {{ element.abstained_perc | number:'1.0-0' }}%</span>
                            }
                            <span class="rm-disagree">{{ 'generic.disagree' | translate }} {{ element.disagree_perc | number:'1.0-0' }}%</span>
                          </div>
                        </div>
                        <div class="rm-stat">
                          <div class="rm-metric-label">{{ 'generic.certainty-degree' | translate }}</div>
                          <div class="rm-stat-value">{{ paraconsistentMetric(element, 'certainty_level_percent') !== null ? (paraconsistentMetric(element, 'certainty_level_percent') | number:'1.0-1') + '%' : '—' }}</div>
                          @if (element.paraconsistent?.metrics?.certainty_direction) {
                            <div class="rm-metric-caption">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.certainty-direction.' + element.paraconsistent.metrics.certainty_direction | translate }}</div>
                          }
                        </div>
                        <div class="rm-stat">
                          <div class="rm-metric-label">{{ 'generic.contradiction-degree' | translate }}</div>
                          <div class="rm-stat-value">{{ paraconsistentMetric(element, 'contradiction_level_percent') !== null ? (paraconsistentMetric(element, 'contradiction_level_percent') | number:'1.0-1') + '%' : '—' }}</div>
                          @if (element.paraconsistent?.metrics?.contradiction_kind) {
                            <div class="rm-metric-caption">{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.contradiction-kind.' + element.paraconsistent.metrics.contradiction_kind | translate }}</div>
                          }
                        </div>
                        <div class="border-top pt-2" style="grid-column: 1 / -1">
                          <div class="d-flex align-items-center justify-content-between">
                            <div class="rm-metric-label">{{ 'components.surveys.paraconsistent-map.title' | translate }}</div>
                            <button mat-icon-button type="button" (click)="element.showMap = !element.showMap"
                              [attr.aria-expanded]="!!element.showMap"
                              [matTooltip]="'components.surveys.paraconsistent-map.' + (element.showMap ? 'hide' : 'show') | translate">
                              <mat-icon>{{ element.showMap ? 'expand_less' : 'scatter_plot' }}</mat-icon>
                            </button>
                          </div>
                          @if (element.showMap) {
                            <app-paraconsistent-map [certainty]="paraconsistentMetric(element, 'certainty_level')"
                              [contradiction]="paraconsistentMetric(element, 'contradiction_level')"
                              [status]="element.status" [tone]="adviceTone(element)"></app-paraconsistent-map>
                          }
                        </div>
                      </div>
                    </div>
                  } @else if (getMetricBlocks(key, element).length > 0) {
                    <div class="rm-metrics-grid">
                      @for (block of getMetricBlocks(key, element); track block.label) {
                        <div class="rm-metric">
                          <div class="rm-metric-label">{{ block.label }}</div>
                          @switch (block.kind) {
                            @case ('stat') {
                              <div class="rm-stat-value">{{ block.value }}</div>
                              @if (block.caption) {
                                <div class="rm-metric-caption">{{ block.caption }}</div>
                              }
                            }
                            @case ('bar-list') {
                              <div class="rm-bar-list">
                                @for (bar of block.bars; track bar.label) {
                                  <div>
                                    <div class="rm-bar-legend">
                                      <span>{{ bar.label }}</span>
                                      <strong>{{ bar.valueLabel }}</strong>
                                    </div>
                                    <div class="rm-bar-track">
                                      <div class="rm-bar-fill" [style.width.%]="bar.pct"></div>
                                    </div>
                                  </div>
                                }
                              </div>
                            }
                            @case ('stat-list') {
                              <div class="rm-stat-list">
                                @for (item of block.items; track item.label) {
                                  <div class="rm-bar-legend">
                                    <span>{{ item.label }}</span>
                                    <strong>{{ item.value }}</strong>
                                  </div>
                                }
                              </div>
                            }
                          }
                        </div>
                      }
                    </div>
                  }

                  <footer class="rm-card-actions">
                    <div class="rm-actions-label">{{ "generic.final-decision" | translate }}</div>
                    @if (element.rewriting) {
                      <div class="rewriting-indicator">
                        <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
                        <span class="rewriting-label">{{ "generic.rewriting" | translate }}</span>
                      </div>
                    } @else {
                      <app-survey-final-decision-selector
                        [element]="element"
                        [survey]="survey"
                        [typology]="key"
                        [recommended]="recommendedDecisions(key, element)"
                        [statuses]="surveyService.questionFinalDecisionStatuses"
                        (decisionChange)="setFinalDecision($event.element, $event.decision)"
                        [appDisableRole]="['VIEWER']" [currentRole]="currentUserInTeam?.role"
                      />
                    }
                  </footer>

                  @if (hasRevisionComparison(element)) {
                    <div class="rm-comparison">
                      <div class="rm-actions-label">
                        <mat-icon>compare_arrows</mat-icon>
                        {{ "components.surveys.survey-dashboard.survey-dashboard.component.revision-comparison-title" | translate }}
                      </div>
                      <div class="rm-comparison-grid">
                        <div>
                          <div class="rm-comparison-label">{{ "components.surveys.survey-dashboard.survey-dashboard.component.original-label" | translate }}</div>
                          <div class="rm-comparison-box rm-comparison-original">{{ element.statement || element.question }}</div>
                        </div>
                        <div>
                          @if (getRevisedStatement(element); as revisedStatement) {
                            <div class="rm-comparison-label">{{ "components.surveys.survey-dashboard.survey-dashboard.component.revised-label" | translate }}</div>
                            <div class="rm-comparison-box rm-comparison-new">{{ revisedStatement }}</div>
                          } @else {
                            @for (split of getSplitStatements(element); track $index) {
                              <div class="rm-comparison-label">{{ "components.surveys.survey-dashboard.survey-dashboard.component.split-statement-label" | translate: { n: $index + 1 } }}</div>
                              <div class="rm-comparison-box rm-comparison-new mb-2">{{ split.question }}</div>
                            }
                          }
                        </div>
                      </div>
                    </div>
                  }
                </article>
              }
              } @empty {
                <div class="rm-empty">
                  <mat-icon>search_off</mat-icon>
                  <span>{{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.no-results' | translate }}</span>
                  <button type="button" class="rm-link-btn" (click)="resetFilters()">
                    {{ 'components.surveys.survey-dashboard.survey-dashboard.component.rm.reset-filters' | translate }}
                  </button>
                </div>
              }
            </div>

            @if (getFilteredQuestions(key).length > 0) {
              <mat-paginator
                class="rm-paginator"
                [length]="getFilteredQuestions(key).length"
                [pageSize]="getPaginationState(key).pageSize"
                [pageIndex]="getPaginationState(key).pageIndex"
                [pageSizeOptions]="[5, 10, 20, 50]"
                showFirstLastButtons
                (page)="onPageChange(key, $event)">
              </mat-paginator>
            }
          </div>
        }
      }

      @if (!survey?.is_revised) {
        <div class="rm-footer">
          <button class="btn-custom text-white" matButton="filled" [disabled]="previewLoading"
            (click)="previewSurveyRevision()" *appHasRole="['OWNER', 'EDITOR']; role: currentUserInTeam?.role">
            @if (previewLoading) {
              <mat-spinner diameter="20" strokeWidth="3" class="button-spinner"></mat-spinner>
            } @else {
              <span>{{ 'modals.modal-survey-details.review-survey' | translate }}</span>
            }
          </button>
        </div>
      }
    </div>
  </section>
}
